/* GASSISA
=========================================================================================
* Descripción: Módulo de Administración de Nóminas
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 07/08/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 10/08/2026 | Lic. José Luis Aragón Cervantes  | Conserva empleados al recalcular nómina
*      3       | 10/08/2026 | Lic. José Luis Aragón Cervantes  | Calcula horas transcurridas para nómina del día actual
*      4       | 10/08/2026 | Lic. José Luis Aragón Cervantes  | Mantiene borrador local y candados de formalización
*      5       | 10/08/2026 | Lic. José Luis Aragón Cervantes  | Reactiva impresión de recibo por PrinterTools
*      6       | 10/08/2026 | Lic. José Luis Aragón Cervantes  | Muestra lista de nómina sin paginado
*      7       | 10/08/2026 | Lic. José Luis Aragón Cervantes  | Despliega lista de nómina sin scroll interno
*      8       | 10/08/2026 | Lic. José Luis Aragón Cervantes  | Agrega vista previa de ticket de recibo
*      9       | 10/08/2026 | Lic. José Luis Aragón Cervantes  | Centra vista previa y envía ticket a PrinterTools
*      10      | 10/08/2026 | Lic. José Luis Aragón Cervantes  | Envía recibos masivos por PrinterTools
*      11      | 10/08/2026 | Lic. José Luis Aragón Cervantes  | Ajusta texto visible de impresión de ticket
*      12      | 10/08/2026 | Lic. José Luis Aragón Cervantes  | Abre buscador de nóminas con Alt+F2
*      13      | 10/08/2026 | Lic. José Luis Aragón Cervantes  | Reimprime nómina desde buscador
*      14      | 10/08/2026 | Lic. José Luis Aragón Cervantes  | Limpia permanencia al crear nómina nueva
 *      15      | 14/08/2026 | Lic. José Luis Aragón Cervantes  | Muestra deducciones y detalle de préstamos por empleado
 *      16      | 19/08/2026 | Lic. José Luis Aragón Cervantes  | Eliminar nómina actual desde Otras acciones (no aplica si esté TERMINADA/TR)
 *      17      | 19/08/2026 | Lic. José Luis Aragón Cervantes  | Corrige agrupación de chequeos del día actual (zona horaria)
 *      18      | 19/08/2026 | Lic. José Luis Aragón Cervantes  | Conceptos de bonificación/deducción por empleado (JSON), editor en cabecera
 *      19      | 19/08/2026 | Lic. José Luis Aragón Cervantes  | Esquema de conceptos global persistido en tabla Parametros (clave ESQUEMA_BONOS_NOMINA)
 *      20      | 22/08/2026 | Lic. José Luis Aragón Cervantes  | Tabla editable con columnas dinémicas, catélogo colapsable, campo bonosJson
 *      21      | 24/10/2026 | Lic. José Luis Aragón Cervantes  | Salario diario por empleado con respaldo general y recálculo por fila
 *      22      | 24/10/2026 | Lic. José Luis Aragón Cervantes  | Conserva el salario diario aplicado al guardar y consultar cada nómina
 * ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Chip, Paper, Stack, Typography } from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import TableViewIcon from "@mui/icons-material/TableView";
import PrintIcon from "@mui/icons-material/Print";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PaymentsIcon from "@mui/icons-material/Payments";

import SizeSoftXLayoutForm from "../leyout/SizeSoftXLayoutForm";
import SizeSoftXModal from "../leyout/SizeSoftXModal";
import SizeSoftXConfirmDialog from "../leyout/SizeSoftXConfirmDialog";
import MnuNominasActions from "./MnuNominasActions";
import SizeSoftXTablaNominas from "../utils/SizeSoftXTablaNominas";
import SizeSoftXTablaNominasEditable from "../utils/SizeSoftXTablaNominasEditable";
import SemaforoStatus from "../utils/SemaforoStatus";
import ModalDetalleChequeosNominas from "./ModalDetalleChequeosNominas";
import ModalHistorialNominas from "./ModalHistorialNominas";
import ModalPrestamoEmpleado from "./ModalPrestamoEmpleado";
import FrmLoginValidate from "./FrmLoginValidate";
import DetalleNominaModal from "./DetalleNominaModal";
import ConceptosEmpleadoEditor from "./ConceptosEmpleadoEditor";
import { useRequest } from "../../context/System/RequestContext";
import { useLayout } from "../../context/System/LayoutContext";
import { useVentas } from "../../context/Ventas/VentasContext";
import { useSesion } from "../../context/System/SesionContext";
import {
    apiTiemposRango,
    apiNominasUsuarios,
    apiNominasComisiones,
    apiNominasFolio,
    apiNominasGuardar,
    apiNominasAplicar,
    apiNominasEliminar,
    apiNominasHistorial,
    apiNominasHistorialDetalle,
    apiNominasParametros,
    apiPrestamosEstadoCuenta,
    apiPrestamosDeduccionesNomina,
    apiReporteNomina,
    apiParametrosGet,
    apiParametrosSave,
} from "../../settings/ApiConfig";

const SALARIO_DIARIO_DEFAULT = 350;
const SALARIO_EMPLEADO_PREFIJO = "NOMINA_SALARIO_DIARIO_";

const CeldaSalarioDiario = ({ fila, onGuardar, deshabilitada }) => {
    const [valor, setValor] = useState(fila.salarioDiarioEmpleado == null ? "" : String(fila.salarioDiarioEmpleado));
    const [guardando, setGuardando] = useState(false);
    useEffect(() => {
        setValor(fila.salarioDiarioEmpleado == null ? "" : String(fila.salarioDiarioEmpleado));
    }, [fila.salarioDiarioEmpleado]);

    const guardar = async () => {
        const numero = Number(valor);
        if (!valor.trim() || !Number.isFinite(numero) || numero <= 0) {
            setValor(fila.salarioDiarioEmpleado == null ? "" : String(fila.salarioDiarioEmpleado));
            return;
        }
        if (numero === fila.salarioDiarioEmpleado) return;
        setGuardando(true);
        const exito = await onGuardar(fila, numero);
        if (!exito) setValor(fila.salarioDiarioEmpleado == null ? "" : String(fila.salarioDiarioEmpleado));
        setGuardando(false);
    };

    return <TextField autoComplete="off" variant="standard" size="small"
        type="text" inputMode="decimal" value={valor}
        disabled={deshabilitada || guardando}
        onChange={(e) => setValor(e.target.value)}
        onBlur={guardar}
        onFocus={(e) => e.target.select()}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
        inputProps={{ "aria-label": `Salario diario de ${fila.nombre}`, style: { textAlign: "right" } }}
        sx={{ width: 100 }} />;
};
const SALARIO_DIARIO_STORAGE_KEY = "sizesoftx-salario-minimo-diario";
const NOMINA_DRAFT_STORAGE_KEY = "sizesoftx-nomina-borrador";
const HORAS_JORNADA = 8;
const MONTO_BONO_PRODUCTIVO_DEFAULT = 0;
const LIMITE_ENTRADA_MINUTOS = 9 * 60 + 15;
const HORA_SALIDA_DEFECTO = "20:00:00";
const MINUTOS_COMIDA = 60;
const STATUS_EN_PROCESO = "EN PROCESO";
const STATUS_TERMINADA = "TERMINADA";
const STATUS_TR = "TR";
const ESQUEMA_BONOS_PARAMETRO = "ESQUEMA_BONOS_NOMINA";

const getDefaultFechaInicio = () => {
    const hoy = new Date();
    hoy.setDate(hoy.getDate() - 6);
    hoy.setHours(0, 0, 0, 0);
    return hoy;
};

const getDefaultFechaFin = () => {
    const hoy = new Date();
    hoy.setHours(23, 59, 59, 999);
    return hoy;
};

const getNominaDraft = () => {
    try {
        const draft = localStorage.getItem(NOMINA_DRAFT_STORAGE_KEY);
        return draft ? JSON.parse(draft) : null;
    } catch (error) {
        return null;
    }
};

const parseNominaDraftDate = (value, fallback, endOfDay = false) => {
    if (!value) return fallback;
    const dateValue = typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
        ? `${value}T00:00:00`
        : value;
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return fallback;
    if (endOfDay) {
        date.setHours(23, 59, 59, 999);
    } else {
        date.setHours(0, 0, 0, 0);
    }
    return date;
};

const formatCurrency = (value) => {
    if (value == null) return "$0.00";
    return new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: "MXN",
    }).format(value);
};

const formatFechaConsulta = (date) => {
    if (!date) return "";
    if (typeof date === "string") return date;
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dia = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dia}`;
};

const formatFechaDisplay = (value) => {
    if (!value) return "";
    const fecha = formatFechaConsulta(value);
    const partes = String(fecha).split("-");
    if (partes.length !== 3) return fecha;
    return `${partes[2]}/${partes[1]}/${partes[0]}`;
};

const formatFechaHoraDisplay = (value) => {
    if (!value) return "";
    const fecha = new Date(value);
    if (isNaN(fecha.getTime())) return String(value);
    return fecha.toLocaleString("es-MX", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const normalizeStartDate = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
};

const normalizeEndDate = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
};

const timeToMinutos = (hora) => {
    if (!hora) return null;
    const partes = String(hora).split(":");
    const h = parseInt(partes[0], 10) || 0;
    const m = parseInt(partes[1], 10) || 0;
    const s = parseInt(partes[2], 10) || 0;
    return h * 60 + m + s / 60;
};

const minutosATime = (minutos) => {
    let m = Math.round(minutos || 0);
    if (m < 0) m = 0;
    if (m > 1440) m = 1440;
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}:00`;
};

const sumarMinutos = (hora, sum) => {
    const base = timeToMinutos(hora);
    if (base == null) return null;
    return minutosATime(base + sum);
};

const formatHoraCorta = (hora) => {
    if (!hora) return "";
    const partes = String(hora).split(":");
    return partes.length >= 2 ? `${partes[0]}:${partes[1]}` : hora;
};

const buildDays = (inicio, fin) => {
    const days = [];
    const actual = new Date(inicio);
    actual.setHours(0, 0, 0, 0);
    const final = new Date(fin);
    final.setHours(0, 0, 0, 0);
    while (actual.getTime() <= final.getTime()) {
        days.push(new Date(actual));
        actual.setDate(actual.getDate() + 1);
    }
    return days;
};

const formatDayKey = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const dia = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${dia}`;
};

const getHoraActual = () => {
    const ahora = new Date();
    return minutosATime(
        ahora.getHours() * 60 +
        ahora.getMinutes() +
        ahora.getSeconds() / 60
    );
};

const isTodayKey = (fechaKey) => {
    if (!fechaKey) return false;
    return fechaKey === formatDayKey(new Date());
};

const groupTiemposPorDia = (registros) => {
    const mapa = {};
    const parsearFechaLocal = (value) => {
        if (value instanceof Date) return new Date(value.getFullYear(), value.getMonth(), value.getDate());
        const texto = String(value || "").trim();
        const match = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
        return new Date(texto);
    };
    (registros || []).forEach((registro) => {
        const fechaRegistro = registro.fecharegistro;
        if (!fechaRegistro) return;
        const date = parsearFechaLocal(fechaRegistro);
        if (isNaN(date.getTime())) return;
        const key = formatDayKey(date);
        if (!mapa[key]) mapa[key] = [];
        mapa[key].push({
            tipo: String(registro.tiporegistro || "").trim(),
            hora: String(registro.hora || "").trim(),
        });
    });
    return mapa;
};

const sumarConceptosPorTipo = (conceptos, tipo) =>
    (conceptos || []).reduce((suma, c) => (String(c?.tipo || "").toUpperCase() === String(tipo).toUpperCase()
        ? suma + (parseFloat(c?.valor) || 0)
        : suma), 0);

const parseEsquema = (valor) => {
    try {
        const parsed = typeof valor === "string" ? JSON.parse(valor) : valor;
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        return [];
    }
};

const clonarEsquema = (esquema) =>
    (esquema || []).map((c) => ({
        tipo: String(c?.tipo || "").toUpperCase(),
        descripcion: String(c?.descripcion || ""),
        valor: Number(c?.valor) || 0,
    }));

const completarChequeosDia = (registrosDia, fechaKey) => {
    const buscar = (tipo) =>
        (registrosDia || []).find((r) => r.tipo.toLowerCase() === String(tipo).toLowerCase());
    const entrada = buscar("Hora Entrada")?.hora || null;
    const salidaComer = buscar("Hora salida a comer")?.hora || null;
    let regresoComida = buscar("Hora entrada de comida")?.hora || null;
    let salida = buscar("Hora salida")?.hora || null;
    const salidaRegistrada = Boolean(salida);
    const esHoy = isTodayKey(fechaKey);
    const horaActual = esHoy ? getHoraActual() : null;
    const inyectados = [];

    if (entrada && !salida) {
        salida = horaActual || HORA_SALIDA_DEFECTO;
        inyectados.push("salida");
    }
    if (salidaComer && !regresoComida) {
        regresoComida = esHoy && !salidaRegistrada ? horaActual : sumarMinutos(salidaComer, MINUTOS_COMIDA);
        inyectados.push("regresoComida");
    }

    let horas = 0;
    if (entrada && salida) {
        const totalMin = timeToMinutos(salida) - timeToMinutos(entrada);
        let comidaMin = 0;
        if (salidaComer && regresoComida) {
            const corrido = timeToMinutos(regresoComida) - timeToMinutos(salidaComer);
            comidaMin = corrido > 0 ? corrido : 0;
        }
        horas = Math.max(0, totalMin - comidaMin) / 60;
    }

    const entradaMin = timeToMinutos(entrada);
    const retardo =
        entrada != null &&
        entradaMin != null &&
        entradaMin > LIMITE_ENTRADA_MINUTOS;

    return {
        entrada,
        salidaComer,
        regresoComida,
        salida,
        horas: Math.round(horas * 100) / 100,
        retardo,
        inyectados,
        trabajo: Boolean(entrada || salida),
    };
};

const constClip = (usuarios, tiemposPorUsuario, dias, salarioDiario, esquemaBonos, comisionesMap, deduccionesMap = {}) =>
    (usuarios || []).map((acceso) => {
        const idacceso = Number(acceso.id || acceso.idacceso || 0);
        const usuario = String(acceso.usuario || "").trim();
        const nombre = String(acceso.nombre || "").trim();
        const mapa = groupTiemposPorDia(tiemposPorUsuario[usuario] || []);
        const chequeosPorDia = dias.map((dia) => {
            const key = formatDayKey(dia);
            return { fecha: key, ...completarChequeosDia(mapa[key] || [], key) };
        });

        const diasTrabajados = chequeosPorDia.filter((dia) => dia.trabajo).length;
        const horasTotales = chequeosPorDia.reduce((suma, dia) => suma + (dia.horas || 0), 0);
        const sinRetardos = chequeosPorDia.every((dia) => !dia.trabajo || !dia.retardo);
        const atendioTodo = dias.length > 0 && diasTrabajados === dias.length;

        const nominabase = (horasTotales / HORAS_JORNADA) * salarioDiario;
        const comisiones = comisionesMap[nombre.toUpperCase()] || 0;
        const deduccionesKey = idacceso > 0 ? String(idacceso) : usuario.toUpperCase();
        const deduccionesPrestamos = deduccionesMap[deduccionesKey] || deduccionesMap[usuario.toUpperCase()] || 0;

        const bonosJson = clonarEsquema(esquemaBonos);
        const bono = sumarConceptosPorTipo(bonosJson, "BONO");
        const deduccionesConceptos = sumarConceptosPorTipo(bonosJson, "DEDUCCION");
        const deducciones = deduccionesPrestamos + deduccionesConceptos;

        return {
            idacceso,
            usuario,
            nombre,
            diasTrabajados,
            totalDias: dias.length,
            horasTotales: Math.round(horasTotales * 100) / 100,
            nominabase,
            bono,
            comisiones,
            deducciones,
            deduccionesPrestamos,
            total: Math.max(0, nominabase + bono + comisiones - deducciones),
            retardos: chequeosPorDia.filter((dia) => dia.trabajo && dia.retardo).length,
            sinRetardos,
            atendioTodo,
            bonosJson,
            chequeosPorDia,
        };
    });

const CustomDateInput = forwardRef(function CustomDateInput(props, ref) {
    const { value, onClick, label } = props;
    return (
        <TextField autoComplete="off"
            label={label}
            size="small"
            value={value || ""}
            onClick={onClick}
            inputRef={ref}
            fullWidth
            InputProps={{ readOnly: true }}
            sx={{ minWidth: 160 }}
        />
    );
});

export default function FrmNominas() {
    const { GetRequest, PostRequest, PostFileRequest } = useRequest();
    const { handleOpenAlert } = useLayout();
    const { handleClickRegresarMainDashboard } = useVentas();
    const { usuarioData, parametrosCajaForm } = useSesion();

    const [fechaInicio, setFechaInicio] = useState(() => {
        const draft = getNominaDraft();
        return parseNominaDraftDate(draft?.fechaInicio, getDefaultFechaInicio());
    });
    const [fechaFin, setFechaFin] = useState(() => {
        const draft = getNominaDraft();
        return parseNominaDraftDate(draft?.fechaFin, getDefaultFechaFin(), true);
    });
    const [esquemaBonos, setEsquemaBonos] = useState(() => parseEsquema(getNominaDraft()?.esquemaBonos));
    const [salarioDiario, setSalarioDiario] = useState(() => {
        const draft = getNominaDraft();
        const draftValor = parseFloat(draft?.salarioDiario);
        if (Number.isFinite(draftValor) && draftValor > 0) return draftValor;
        const stored = localStorage.getItem(SALARIO_DIARIO_STORAGE_KEY);
        const valor = parseFloat(stored);
        return Number.isFinite(valor) && valor > 0 ? valor : SALARIO_DIARIO_DEFAULT;
    });
    const [usuarios, setUsuarios] = useState(() => getNominaDraft()?.usuarios || []);
    const [nominaRows, setNominaRows] = useState(() => getNominaDraft()?.nominaRows || null);
    const [horasJornada, setHorasJornada] = useState(HORAS_JORNADA);
    const [montoBonoProductivo, setMontoBonoProductivo] = useState(MONTO_BONO_PRODUCTIVO_DEFAULT);
    const [loading, setLoading] = useState(false);
    const salariosPendientes = useRef(0);
    const [chequeoFila, setChequeoFila] = useState(null);
    const [nominaInfo, setNominaInfo] = useState(() => getNominaDraft()?.nominaInfo || { folio: 0, id: 0, status: "" });
    const [historialAbierto, setHistorialAbierto] = useState(false);
    const [historialRows, setHistorialRows] = useState([]);
    const [cargandoHistorial, setCargandoHistorial] = useState(false);
    const [detalleModal, setDetalleModal] = useState(null);
    const [openLoginPrestamo, setOpenLoginPrestamo] = useState(false);
    const [openPrestamo, setOpenPrestamo] = useState(false);
    const [usuarioAutorizaPrestamo, setUsuarioAutorizaPrestamo] = useState("");
    const [deduccionesDetalle, setDeduccionesDetalle] = useState(null);
    const [loadingDeducciones, setLoadingDeducciones] = useState(false);
    const [confirmEliminar, setConfirmEliminar] = useState(false);

    useEffect(() => {
        cargarUsuarios();
    }, []);

    useEffect(() => {
        cargarEsquemaBonos();
    }, []);

    useEffect(() => {
        cargarParametros();
    }, []);

    useEffect(() => {
        if (nominaRows === null) return;
        const draft = {
            fechaInicio: formatFechaConsulta(fechaInicio),
            fechaFin: formatFechaConsulta(fechaFin),
            salarioDiario,
            usuarios,
            nominaRows,
            nominaInfo,
            esquemaBonos,
        };
        localStorage.setItem(NOMINA_DRAFT_STORAGE_KEY, JSON.stringify(draft));
    }, [fechaInicio, fechaFin, salarioDiario, usuarios, nominaRows, nominaInfo, esquemaBonos]);

    const handleChangeSalarioDiario = (e) => {
        const valor = parseFloat(e.target.value);
        const salario = Number.isFinite(valor) && valor > 0 ? valor : SALARIO_DIARIO_DEFAULT;
        setSalarioDiario(salario);
        setNominaRows((prev) => prev?.map((fila) => {
            if (nominaInfo?.status === STATUS_TERMINADA || fila.salarioDiarioEmpleado == null || fila.salarioIndividual) return fila;
            const nominabase = (Number(fila.horasTotales || 0) / horasJornada) * salario;
            return { ...fila, salarioDiarioEmpleado: salario, nominabase,
                total: Math.max(0, nominabase + (fila.bono || 0) + (fila.comisiones || 0) - (fila.deducciones || 0)) };
        }) ?? prev);
        localStorage.setItem(SALARIO_DIARIO_STORAGE_KEY, String(salario));
        guardarParametrosGlobales({ salario });
    };

    const handleChangeHorasJornada = (e) => {
        const valor = parseInt(e.target.value, 10);
        const horas = Number.isInteger(valor) && valor > 0 ? valor : HORAS_JORNADA;
        setHorasJornada(horas);
        guardarParametrosGlobales({ horas });
    };

    const cargarUsuarios = useCallback(async () => {
        const respuesta = await GetRequest({
            url: apiNominasUsuarios,
            loader: "Cargando usuarios para nómina...",
        });
        if (!respuesta.hasError && respuesta.result) {
            setUsuarios(respuesta.result || []);
        } else if (respuesta.hasError) {
            handleOpenAlert("Error al cargar usuarios: " + respuesta.message, "error");
        }
    }, [GetRequest, handleOpenAlert]);

    const cargarEsquemaBonos = useCallback(async () => {
        try {
            const respuesta = await GetRequest({
                url: apiParametrosGet.replace("{nombre}", ESQUEMA_BONOS_PARAMETRO),
                loader: "Cargando esquema de conceptos...",
            });
            if (!respuesta.hasError && respuesta.result != null) {
                setEsquemaBonos(parseEsquema(respuesta.result));
            }
        } catch (error) {
            handleOpenAlert("Error al cargar el esquema de conceptos: " + error.message, "error");
        }
    }, [GetRequest, handleOpenAlert]);

    const cargarParametros = useCallback(async () => {
        try {
            const respuesta = await GetRequest({
                url: apiNominasParametros,
                loader: "Cargando parémetros de nómina...",
            });
            if (!respuesta.hasError && respuesta.result) {
                const parametros = respuesta.result;
                const salario = Number(parametros.salariominimo);
                if (Number.isFinite(salario) && salario > 0) {
                    const sinOverrideLocal = !localStorage.getItem(SALARIO_DIARIO_STORAGE_KEY)
                        && !(getNominaDraft()?.salarioDiario);
                    if (sinOverrideLocal) setSalarioDiario(salario);
                }
                const horas = Number(parametros.horaslaboralesdia);
                if (Number.isInteger(horas) && horas > 0) setHorasJornada(horas);
                const bono = Number(parametros.montobonoproductivo);
                if (Number.isFinite(bono) && bono >= 0) setMontoBonoProductivo(bono);
            }
        } catch (error) {
            handleOpenAlert("Error al cargar parémetros de nómina: " + error.message, "error");
        }
    }, [GetRequest, handleOpenAlert]);

    const guardarParametrosGlobales = async ({ salario = null, horas = null } = {}) => {
        try {
            await PostRequest({
                url: apiNominasParametros,
                body: {
                    salariominimo: Number.isFinite(salario) && salario > 0 ? salario : salarioDiario,
                    horaslaboralesdia: Number.isInteger(horas) && horas > 0 ? horas : horasJornada,
                    montobonoproductivo: Number(montoBonoProductivo) || 0,
                    usuario: usuarioData?.Usuario || "",
                },
            });
        } catch (error) {
            handleOpenAlert("Error al guardar parémetros de nómina: " + error.message, "error");
        }
    };

    const periodoLabel = () =>
        `${formatFechaDisplay(fechaInicio)} al ${formatFechaDisplay(fechaFin)}`;

    const parseChequeos = (value) => {
        try {
            const arr = JSON.parse(value || "[]");
            return Array.isArray(arr) ? arr : [];
        } catch (e) {
            return [];
        }
    };

    const handleNueva = async () => {
        if (salariosPendientes.current) {
            handleOpenAlert("Espera a que termine el guardado de los salarios", "warning");
            return;
        }
        if (nominaRows?.length && nominaInfo?.status !== STATUS_TERMINADA) {
            handleOpenAlert("Primero guarda y aplica la nómina en proceso antes de crear una nueva", "warning");
            return;
        }
        const respuesta = await GetRequest({
            url: apiNominasFolio,
            loader: "Obteniendo folio disponible...",
        });
        if (respuesta.hasError) {
            handleOpenAlert("No fue posible obtener el folio: " + respuesta.message, "error");
            return;
        }
        const folio = respuesta.result?.folio || 0;
        const nuevaFechaInicio = getDefaultFechaInicio();
        const nuevaFechaFin = getDefaultFechaFin();
        localStorage.removeItem(NOMINA_DRAFT_STORAGE_KEY);
        setFechaInicio(nuevaFechaInicio);
        setFechaFin(nuevaFechaFin);
        setChequeoFila(null);
        setDetalleModal(null);
        setHistorialAbierto(false);

        const usuariosResp = await GetRequest({
            url: apiNominasUsuarios,
            loader: "Cargando accesos activos...",
        });
        let listaUsuarios = [];
        if (!usuariosResp.hasError && Array.isArray(usuariosResp.result)) {
            listaUsuarios = usuariosResp.result;
            setUsuarios(listaUsuarios);
        } else {
            handleOpenAlert(
                "No se cargaron los accesos activos: " + (usuariosResp.message || "respuesta invélida"),
                "error"
            );
        }

        const dias = buildDays(nuevaFechaInicio, nuevaFechaFin);
        const filasCero = listaUsuarios.map((acceso) => ({
            idacceso: Number(acceso.id || acceso.idacceso || 0),
            salarioIndividual: acceso.salariodiario != null,
            salarioDiarioEmpleado: Number(acceso.salariodiario) || salarioDiario,
            usuario: String(acceso.usuario || "").trim(),
            nombre: String(acceso.nombre || "").trim(),
            diasTrabajados: 0,
            totalDias: dias.length,
            horasTotales: 0,
            nominabase: 0,
            bono: 0,
            comisiones: 0,
            deducciones: 0,
            deduccionesPrestamos: 0,
            total: 0,
            retardos: 0,
            sinRetardos: true,
            atendioTodo: false,
            bonosJson: clonarEsquema(esquemaBonos),
            chequeosPorDia: dias.map((dia) => ({
                fecha: formatDayKey(dia),
                entrada: null,
                salidaComer: null,
                regresoComida: null,
                salida: null,
                horas: 0,
                retardo: false,
                inyectados: [],
                trabajo: false,
            })),
        }));

        setNominaInfo({ folio, id: 0, status: STATUS_EN_PROCESO });
        setNominaRows(filasCero);
        setDetalleModal(null);
        handleOpenAlert(
            folio
                ? `Nueva nómina, folio ${folio} — ${filasCero.length} accesos activos`
                : "No se obtuvo folio disponible",
            "success"
        );
    };

    const abrirHistorial = async () => {
        setHistorialAbierto(true);
        setCargandoHistorial(true);
        try {
            const params = `fechainicio=${formatFechaConsulta(fechaInicio)}&fechafin=${formatFechaConsulta(fechaFin)}`;
            const respuesta = await GetRequest({
                url: `${apiNominasHistorial}?${params}`,
                loader: "Cargando historial de nóminas...",
            });
            setHistorialRows(respuesta.hasError ? [] : respuesta.result || []);
            if (respuesta.hasError) {
                handleOpenAlert("No se pudo consultar el historial: " + respuesta.message, "error");
            }
        } catch (error) {
            setHistorialRows([]);
            handleOpenAlert("Error al consultar historial: " + error.message, "error");
        } finally {
            setCargandoHistorial(false);
        }
    };

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.altKey && event.key === "F2") {
                event.preventDefault();
                abrirHistorial();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [abrirHistorial]);

    const consultarDetalle = async (fila) => {
        const respuesta = await GetRequest({
            url: apiNominasHistorialDetalle.replace("{id}", String(fila.id)),
            loader: "Cargando detalle de la nómina...",
        });
        if (respuesta.hasError) {
            handleOpenAlert("No se pudo consultar el detalle: " + respuesta.message, "error");
            return null;
        }
        return respuesta.result;
    };

    const verDetalle = async (fila) => {
        const detalle = await consultarDetalle(fila);
        if (detalle) setDetalleModal(detalle);
    };

    const editarDesdeHistorial = async (fila) => {
        const detalle = await consultarDetalle(fila);
        if (!detalle) return;
        const header = detalle.header || {};
        const inicioStr = header.fechainicio ? `${header.fechainicio}T00:00:00` : "2020-01-01T00:00:00";
        const finStr = header.fechafin ? `${header.fechafin}T00:00:00` : "2020-01-01T00:00:00";
        const totalDias = buildDays(new Date(inicioStr), new Date(finStr)).length;
        const filas = (detalle.detalle || []).map((empleado) => {
            const bonosJson = parseChequeos(empleado.conceptos);
            return {
                idacceso: Number(empleado.idacceso || empleado.id || 0),
                salarioDiarioEmpleado: empleado.salariodiario == null ? null : Number(empleado.salariodiario),
                salarioIndividual: true,
                usuario: empleado.usuario || "",
                nombre: empleado.nombre || "",
                diasTrabajados: empleado.dias || 0,
                totalDias,
                horasTotales: Number(empleado.horas || 0),
                nominabase: Number(empleado.nominabase || 0),
                bono: Number(empleado.bono || 0),
                comisiones: Number(empleado.comisiones || 0),
                deducciones: Number(empleado.deducciones || 0),
                deduccionesPrestamos: Math.max(0, Number(empleado.deducciones || 0) - sumarConceptosPorTipo(bonosJson, "DEDUCCION")),
                total: Number(empleado.total || 0),
                retardos: empleado.retardos || 0,
                bonosJson,
                chequeosPorDia: parseChequeos(empleado.chequeos),
            };
        });
        setNominaInfo({ folio: fila.folio, id: fila.id, status: header.status || "" });
        setFechaInicio(new Date(inicioStr));
        setFechaFin(new Date(finStr));
        setNominaRows(filas);
        setHistorialAbierto(false);
        const esquemaGuardado = filas.find((f) => Array.isArray(f?.bonosJson) && f.bonosJson.length > 0)?.bonosJson;
        if (esquemaGuardado) setEsquemaBonos(clonarEsquema(esquemaGuardado));
        handleOpenAlert(`Nómina folio ${fila.folio} cargada para edición`, "success");
    };

    const handleAplicar = async () => {
        const infoAplicar = await guardarNomina({ mostrarAlerta: false });
        if (!infoAplicar?.id) {
            handleOpenAlert("No fue posible guardar la nómina para aplicarla", "error");
            return;
        }
        const respuesta = await PostRequest({
            url: apiNominasAplicar,
            body: { id: infoAplicar.id },
            loader: "Aplicando nómina...",
        });
        if (respuesta.hasError) {
            handleOpenAlert("Error al aplicar: " + respuesta.message, "error");
            return;
        }
        setNominaInfo((prev) => ({ ...prev, id: infoAplicar.id, folio: infoAplicar.folio, status: STATUS_TERMINADA }));
        handleOpenAlert(`Nómina folio ${infoAplicar.folio} aplicada (TERMINADA)`, "success");
    };

    const handleCalcular = async () => {
        if (salariosPendientes.current) {
            handleOpenAlert("Espera a que termine el guardado de los salarios", "warning");
            return;
        }
        if (!fechaInicio || !fechaFin) {
            handleOpenAlert("Selecciona las fechas del periodo", "warning");
            return;
        }
        if (fechaInicio.getTime() > fechaFin.getTime()) {
            handleOpenAlert("La fecha de inicio no puede ser mayor a la de fin", "warning");
            return;
        }

        setLoading(true);
        try {
            const usuariosResp = await GetRequest({ url: apiNominasUsuarios });
            if (usuariosResp.hasError || !usuariosResp.result) {
                throw new Error(usuariosResp.message || "No fue posible consultar los salarios de los empleados");
            }
            const lista = usuariosResp.result;
            setUsuarios(lista);

            const fechainicio = formatFechaConsulta(fechaInicio);
            const fechafin = formatFechaConsulta(fechaFin);
            const tiemposPorUsuario = {};

            for (const acceso of lista || []) {
                const usuario = String(acceso.usuario || "").trim();
                if (!usuario) continue;
                const url = apiTiemposRango
                    .replace("{usuario}", encodeURIComponent(usuario))
                    .replace("{fechainicio}", fechainicio)
                    .replace("{fechafin}", fechafin);
                const respuesta = await GetRequest({ url });
                tiemposPorUsuario[usuario] = respuesta.hasError ? [] : respuesta.result || [];
            }

            const comisionesResp = await PostRequest({
                url: apiNominasComisiones,
                body: { fechainicio, fechafin },
                loader: "Calculando comisiones...",
            });
            const comisionesMap = {};
            (comisionesResp.hasError ? [] : comisionesResp.result || []).forEach((comision) => {
                comisionesMap[String(comision.nombre || "").trim().toUpperCase()] = Number(
                    comision.totalcomision || 0
                );
            });

            const deduccionesResp = await GetRequest({ url: apiPrestamosDeduccionesNomina });
            const deduccionesMap = {};
            (deduccionesResp.hasError ? [] : deduccionesResp.result || []).forEach((deduccion) => {
                const deduccionId = Number(deduccion.idacceso || deduccion.idaccesoempleado || 0);
                const deduccionValor = Number(deduccion.deducciones || 0);
                if (deduccionId > 0) deduccionesMap[String(deduccionId)] = deduccionValor;
                deduccionesMap[String(deduccion.usuario || "").trim().toUpperCase()] = deduccionValor;
            });

            const dias = buildDays(fechaInicio, fechaFin);
            const filas = (lista || []).map((acceso) => {
                const idacceso = Number(acceso.id || acceso.idacceso || 0);
                const usuario = String(acceso.usuario || "").trim();
                const nombre = String(acceso.nombre || "").trim();
                const mapa = groupTiemposPorDia(tiemposPorUsuario[usuario] || []);
                const chequeosPorDia = dias.map((dia) => {
                    const key = formatDayKey(dia);
                    return { fecha: key, ...completarChequeosDia(mapa[key] || [], key) };
                });

                const diasTrabajados = chequeosPorDia.filter((dia) => dia.trabajo).length;
                const horasTotales = chequeosPorDia.reduce((suma, dia) => suma + (dia.horas || 0), 0);
                const sinRetardos = chequeosPorDia.every((dia) => !dia.trabajo || !dia.retardo);
                const atendioTodo = dias.length > 0 && diasTrabajados === dias.length;

                const salarioEmpleado = Number(acceso.salariodiario) || salarioDiario;
                const nominabase = (horasTotales / horasJornada) * salarioEmpleado;
                const comisiones = comisionesMap[nombre.toUpperCase()] || 0;
                const deduccionesKey = idacceso > 0 ? String(idacceso) : usuario.toUpperCase();
                const deduccionesPrestamos = deduccionesMap[deduccionesKey] || deduccionesMap[usuario.toUpperCase()] || 0;

                const bonosJsonPrevio = (nominaRows || []).find((r) => String(r.usuario || "").trim() === usuario)?.bonosJson;
                const bonosJson = Array.isArray(bonosJsonPrevio) && bonosJsonPrevio.length > 0
                    ? bonosJsonPrevio
                    : clonarEsquema(esquemaBonos);
                const bono = sumarConceptosPorTipo(bonosJson, "BONO");
                const deduccionesConceptos = sumarConceptosPorTipo(bonosJson, "DEDUCCION");
                const deducciones = deduccionesPrestamos + deduccionesConceptos;

                return {
                    idacceso,
                    salarioIndividual: acceso.salariodiario != null,
                    salarioDiarioEmpleado: salarioEmpleado,
                    usuario,
                    nombre,
                    diasTrabajados,
                    totalDias: dias.length,
                    horasTotales: Math.round(horasTotales * 100) / 100,
                    nominabase,
                    bono,
                    comisiones,
                    deducciones,
                    deduccionesPrestamos,
                    total: Math.max(0, nominabase + bono + comisiones - deducciones),
                    retardos: chequeosPorDia.filter((dia) => dia.trabajo && dia.retardo).length,
                    sinRetardos,
                    atendioTodo,
                    bonosJson,
                    chequeosPorDia,
                };
            });

            setNominaRows(filas);
            if (filas.length === 0) {
                handleOpenAlert("No se encontraron empleados para calcular", "warning");
            } else {
                handleOpenAlert(`Nómina calculada: ${filas.length} empleados`, "success");
            }
        } catch (error) {
            handleOpenAlert("Error al calcular nómina: " + error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    const guardarNomina = async ({ mostrarAlerta = true } = {}) => {
        if (salariosPendientes.current) {
            handleOpenAlert("Espera a que termine el guardado de los salarios", "warning");
            return null;
        }
        if (!nominaRows || nominaRows.length === 0) {
            handleOpenAlert("Primero calcula la nómina", "warning");
            return null;
        }
        const body = {
            folio: nominaInfo?.folio || 0,
            fechainicio: formatFechaConsulta(fechaInicio),
            fechafin: formatFechaConsulta(fechaFin),
            usuario: usuarioData?.Usuario || "",
            empleados: nominaRows.map((fila) => ({
                idacceso: Number(fila.idacceso || 0),
                salariodiario: fila.salarioDiarioEmpleado == null ? null : Number(fila.salarioDiarioEmpleado),
                usuario: fila.usuario,
                nombre: fila.nombre,
                dias: fila.diasTrabajados,
                horas: Number(fila.horasTotales.toFixed(2)),
                nominabase: Number(fila.nominabase.toFixed(2)),
                bono: Number(fila.bono.toFixed(2)),
                comisiones: Number(fila.comisiones.toFixed(2)),
                deducciones: Number((fila.deducciones || 0).toFixed(2)),
                total: Number(fila.total.toFixed(2)),
                retardos: fila.retardos,
                chequeos: JSON.stringify(fila.chequeosPorDia || []),
                conceptos: JSON.stringify(fila.bonosJson || []),
            })),
        };

        try {
            const respuesta = await PostRequest({
                url: apiNominasGuardar,
                body,
                loader: "Guardando nómina...",
            });
            if (!respuesta.hasError) {
                const guardado = respuesta.result || {};
                const info = {
                    folio: guardado.folio || nominaInfo?.folio,
                    id: guardado.id || nominaInfo?.id || 0,
                    status: STATUS_EN_PROCESO,
                };
                setNominaInfo(info);
                if (mostrarAlerta) {
                    handleOpenAlert(
                        "Nómina guardada correctamente" + (guardado.folio ? ` — Folio ${guardado.folio}` : ""),
                        "success"
                    );
                }
                return info;
            }
            handleOpenAlert("Error al guardar: " + respuesta.message, "error");
        } catch (error) {
            handleOpenAlert("Error al guardar nómina: " + error.message, "error");
        }
        return null;
    };

    const handleGuardar = async () => {
        await guardarNomina();
    };

    const statusActual = nominaInfo?.status || "";
    const nominaEliminable =
        statusActual !== STATUS_TERMINADA &&
        statusActual !== STATUS_TR &&
        nominaInfo?.id > 0;

    const handleEliminarNomina = () => {
        if (statusActual === STATUS_TERMINADA || statusActual === STATUS_TR) {
            handleOpenAlert("No se puede eliminar una nómina TERMINADA", "warning");
            return;
        }
        if (!nominaInfo?.id && !nominaRows?.length) {
            handleOpenAlert("No hay nómina actual para eliminar", "warning");
            return;
        }
        if (!nominaInfo?.id) {
            handleOpenAlert("La nómina actual aún no se ha guardado, no hay registro en el servidor", "warning");
            return;
        }
        setConfirmEliminar(true);
    };

    const handleConfirmEliminar = async (isConfirmed) => {
        setConfirmEliminar(false);
        if (!isConfirmed) return;
        const id = nominaInfo?.id;
        if (!id) {
            handleOpenAlert("No hay nómina guardada para eliminar", "warning");
            return;
        }
        try {
            const respuesta = await PostRequest({
                url: apiNominasEliminar,
                body: { id },
                loader: "Eliminando nómina...",
            });
            if (respuesta.hasError) {
                handleOpenAlert("Error al eliminar la nómina: " + respuesta.message, "error");
                return;
            }
            localStorage.removeItem(NOMINA_DRAFT_STORAGE_KEY);
            setNominaInfo({ folio: 0, id: 0, status: "" });
            setNominaRows(null);
            setChequeoFila(null);
            setDetalleModal(null);
            handleOpenAlert(`Nómina folio ${nominaInfo?.folio || ""} eliminada`, "success");
        } catch (error) {
            handleOpenAlert("Error al eliminar la nómina: " + error.message, "error");
        }
    };

    const aplicarEsquemaANomina = (rows, esquema) => {
        const nuevoEsquema = clonarEsquema(esquema);
        return (rows || []).map((fila) => {
            const bonosJsonExistentes = Array.isArray(fila.bonosJson) ? fila.bonosJson : [];
            const bonosJson = nuevoEsquema.map((nuevo) => {
                const existente = bonosJsonExistentes.find(
                    (c) => String(c.descripcion || "").trim().toLowerCase() === String(nuevo.descripcion || "").trim().toLowerCase()
                        && String(c.tipo || "").trim().toUpperCase() === String(nuevo.tipo || "").trim().toUpperCase()
                );
                return existente ? { ...nuevo, valor: Number(existente.valor) || 0 } : { ...nuevo, valor: 0 };
            });
            const bono = sumarConceptosPorTipo(bonosJson, "BONO");
            const deduccionesConceptos = sumarConceptosPorTipo(bonosJson, "DEDUCCION");
            const deducciones = (fila.deduccionesPrestamos || 0) + deduccionesConceptos;
            return {
                ...fila,
                bonosJson,
                bono,
                deducciones,
                total: Math.max(0, (fila.nominabase || 0) + bono + (fila.comisiones || 0) - deducciones),
            };
        });
    };

    const guardarEsquemaBonos = async (nuevoEsquema) => {
        try {
            await PostRequest({
                url: apiParametrosSave,
                body: {
                    nombre: ESQUEMA_BONOS_PARAMETRO,
                    valor: JSON.stringify(clonarEsquema(nuevoEsquema)),
                },
                loader: "Guardando esquema de conceptos...",
            });
        } catch (error) {
            handleOpenAlert("Error al guardar el esquema de conceptos: " + error.message, "error");
        }
    };

    const handleCambiarEsquema = (nuevoEsquema, persistir = false) => {
        setEsquemaBonos(clonarEsquema(nuevoEsquema));
        setNominaRows((prev) => aplicarEsquemaANomina(prev, nuevoEsquema));
        if (persistir) guardarEsquemaBonos(nuevoEsquema);
    };

    const handleEditarConceptoEmpleado = useCallback((fila, nuevosConceptos) => {
        const usuario = String(fila.usuario || "").trim();
        setNominaRows((prev) =>
            (prev || []).map((r) => {
                if (String(r.usuario || "").trim() !== usuario) return r;
                const bonosJson = clonarEsquema(nuevosConceptos);
                const bono = sumarConceptosPorTipo(bonosJson, "BONO");
                const deduccionesConceptos = sumarConceptosPorTipo(bonosJson, "DEDUCCION");
                const deducciones = (r.deduccionesPrestamos || 0) + deduccionesConceptos;
                return {
                    ...r,
                    bonosJson,
                    bono,
                    deducciones,
                    total: Math.max(0, (r.nominabase || 0) + bono + (r.comisiones || 0) - deducciones),
                };
            })
        );
    }, []);

    const getPrinterTicketNomina = () =>
        parametrosCajaForm?.nombreimpresoraapartados?.trim() ||
        parametrosCajaForm?.nombreimpresoraventas?.trim() ||
        parametrosCajaForm?.nombreimpresorasventas?.trim() ||
        localStorage.getItem("nombreimpresoraapartados") ||
        localStorage.getItem("nombreimpresoraventas") ||
        "";

    const getTicketNominaLineas = (fila) => {
        const encabezado = (parametrosCajaForm?.encabezadoticket || "SIZESOFTX").trim();
        const pie = (parametrosCajaForm?.pieticket || "").trim();
        const lineas = [
            encabezado,
            "--------------------------------",
            "RECIBO DE NOMINA",
            `Periodo: ${periodoLabel()}`,
            `Empleado: ${fila.nombre} (${fila.usuario})`,
            "--------------------------------",
            `Dias: ${fila.diasTrabajados} de ${fila.totalDias}`,
            `Horas: ${fila.horasTotales.toFixed(2)}`,
            "--------------------------------",
            `Nomina:     ${formatCurrency(fila.nominabase)}`,
            `Bono:       ${formatCurrency(fila.bono)}`,
            `Comisiones: ${formatCurrency(fila.comisiones)}`,
            `Deducciones:${formatCurrency(fila.deducciones || 0)}`,
            "--------------------------------",
            `TOTAL:      ${formatCurrency(fila.total)}`,
            "--------------------------------",
            "Salvo buen cobro",
            "Firma: _______________________",
            "Fecha: " + new Date().toLocaleDateString("es-MX"),
        ];
        if (pie) lineas.push(pie);
        lineas.push("Vuelva pronto");
        return lineas;
    };

    const getTicketBytes = (lineas) => {
        const textEncoder = new TextEncoder();
        const ticketParts = [
            textEncoder.encode("\x1B\x40"),
            textEncoder.encode("\x1B\x33\x00"),
            textEncoder.encode("\x1B\x61\x01\x1B\x21\x00" + lineas[0] + "\n"),
            textEncoder.encode("\x1B\x32"),
            ...lineas.slice(1).map((linea) => textEncoder.encode(linea + "\n")),
            textEncoder.encode("\n\n\n\n"),
            textEncoder.encode("\x1D\x56\x00"),
        ];
        const totalLength = ticketParts.reduce((suma, parte) => suma + parte.length, 0);
        const finalTicket = new Uint8Array(totalLength);
        let offset = 0;
        ticketParts.forEach((parte) => {
            finalTicket.set(parte, offset);
            offset += parte.length;
        });
        return finalTicket;
    };

    const getTicketNominaBytes = (fila) => getTicketBytes(getTicketNominaLineas(fila));

    const getPrestamosActivos = (prestamos) =>
        (prestamos || []).filter((prestamo) => Number(prestamo.saldopendiente || 0) > 0);

    const getTicketDeduccionesLineas = () => {
        const fila = deduccionesDetalle?.fila;
        const prestamos = getPrestamosActivos(deduccionesDetalle?.estadoCuenta?.prestamos);
        const encabezado = (parametrosCajaForm?.encabezadoticket || "SIZESOFTX").trim();
        const pie = (parametrosCajaForm?.pieticket || "").trim();
        const lineas = [
            encabezado,
            "--------------------------------",
            "RESUMEN DEDUCCIONES NOMINA",
            `Periodo: ${periodoLabel()}`,
            `Empleado: ${fila?.nombre || ""}`,
            `Clave: ${fila?.usuario || ""}`,
            "--------------------------------",
            `Deduccion periodo: ${formatCurrency(fila?.deducciones || 0)}`,
            "--------------------------------",
            "PRESTAMOS ACTIVOS",
        ];

        if (prestamos.length === 0) {
            lineas.push("Sin prestamos activos");
        } else {
            prestamos.forEach((prestamo) => {
                lineas.push(`Folio: ${prestamo.folioprestamo}`);
                lineas.push(`Fecha: ${formatFechaHoraDisplay(prestamo.fechaprestamo)}`);
                lineas.push(`Concepto: ${prestamo.concepto || ""}`);
                if (String(prestamo.tipoprestamo || "").trim().toUpperCase() === "MERCANCIA") {
                    lineas.push(`Articulo: ${prestamo.clavearticulo || ""}`);
                    lineas.push(`Medida: ${prestamo.medida || ""} Cant: ${prestamo.cantidad || ""}`);
                }
                lineas.push(`Monto: ${formatCurrency(prestamo.montoprestamo || 0)}`);
                lineas.push(`Abonado: ${formatCurrency(prestamo.montoabonado || 0)}`);
                lineas.push(`Saldo: ${formatCurrency(prestamo.saldopendiente || 0)}`);
                lineas.push("--------------------------------");
            });
        }

        const saldoActivo = prestamos.reduce((total, prestamo) => total + Number(prestamo.saldopendiente || 0), 0);
        lineas.push(`Saldo prestamos: ${formatCurrency(saldoActivo)}`);
        lineas.push("Fecha: " + new Date().toLocaleDateString("es-MX"));
        if (pie) lineas.push(pie);
        return lineas;
    };

    const escapeHtml = (value) =>
        String(value ?? "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");

    const abrirVistaPreviaTicketNomina = (fila) => {
        if (!fila) return;
        const ancho = 420;
        const alto = 720;
        const left = window.screenX + Math.max(0, (window.outerWidth - ancho) / 2);
        const top = window.screenY + Math.max(0, (window.outerHeight - alto) / 2);
        const ventana = window.open(
            "",
            "_blank",
            `width=${ancho},height=${alto},left=${Math.round(left)},top=${Math.round(top)}`
        );
        if (!ventana) {
            handleOpenAlert("El navegador bloqueó la vista previa del ticket", "warning");
            return;
        }
        const ticketPreview = escapeHtml(getTicketNominaLineas(fila).join("\n"));
        ventana.document.write(`<!DOCTYPE html><html lang="es"><head><meta charset="utf-8"/><title>Vista previa recibo nómina</title>
        <style>
          body { margin: 0; background: #eceff1; color: #111; font-family: Arial, Helvetica, sans-serif; }
          .toolbar { padding: 10px; text-align: right; background: #fff; border-bottom: 1px solid #ddd; position: sticky; top: 0; }
          .ticket { width: 302px; min-height: 520px; margin: 18px auto; padding: 14px 12px; background: #fff; box-shadow: 0 2px 10px rgba(0,0,0,.18); }
          pre { margin: 0; white-space: pre-wrap; font-family: Consolas, "Courier New", monospace; font-size: 12px; line-height: 1.35; }
          button { border: 1px solid #1976d2; background: #1976d2; color: #fff; padding: 6px 12px; cursor: pointer; }
          @media print { body { background: #fff; } .toolbar { display: none; } .ticket { box-shadow: none; margin: 0; width: auto; } }
        </style></head><body>
        <div class="toolbar"><button id="btn-imprimir-printertools" type="button">Imprimir Ticket</button></div>
        <div class="ticket"><pre>${ticketPreview}</pre></div>
        </body></html>`);
        ventana.document.close();
        ventana.focus();
        ventana.document
            .getElementById("btn-imprimir-printertools")
            ?.addEventListener("click", () => imprimirTicketNomina(fila));
    };

    const enviarTicketNominaPrinterTools = async (fila) => {
        const printerName = getPrinterTicketNomina();
        if (!printerName) {
            throw new Error("No hay impresora configurada para tickets de nómina");
        }

        const formData = new FormData();
        formData.append("ticket", new Blob([getTicketNominaBytes(fila)], { type: "application/octet-stream" }));
        formData.append("printerName", printerName);
        localStorage.setItem("nombreimpresoraventas", printerName);

        const logoUrl = `${process.env.PUBLIC_URL}/recursos/logocliente.png`;
        try {
            const logoResponse = await fetch(logoUrl);
            if (logoResponse.ok) {
                const logoBlob = await logoResponse.blob();
                formData.append("logo", logoBlob, "logocliente.png");
            }
        } catch (err) {
            // Logo no disponible, continuar sin él.
        }

        const response = await fetch("http://localhost:4000/api/imprimirticket", {
            method: "POST",
            body: formData,
        });

        if (!response.ok) {
            throw new Error(response.statusText || "No se pudo imprimir el recibo");
        }
    };

    const enviarTicketDeduccionesPrinterTools = async () => {
        const printerName = getPrinterTicketNomina();
        if (!printerName) {
            throw new Error("No hay impresora configurada para tickets de nómina");
        }

        const formData = new FormData();
        formData.append("ticket", new Blob([getTicketBytes(getTicketDeduccionesLineas())], { type: "application/octet-stream" }));
        formData.append("printerName", printerName);
        localStorage.setItem("nombreimpresoraventas", printerName);

        const response = await fetch("http://localhost:4000/api/imprimirticket", {
            method: "POST",
            body: formData,
        });

        if (!response.ok) {
            throw new Error(response.statusText || "No se pudo imprimir el resumen de deducciones");
        }
    };

    const imprimirTicketDeducciones = async () => {
        if (!deduccionesDetalle?.fila) return;
        try {
            await enviarTicketDeduccionesPrinterTools();
            handleOpenAlert("Resumen de deducciones enviado a la impresora", "success");
        } catch (error) {
            handleOpenAlert("Error al imprimir deducciones: " + error.message, "error");
        }
    };

    const abrirDeduccionesEmpleado = async (fila) => {
        if (!fila) return;
        setLoadingDeducciones(true);
        setDeduccionesDetalle({ fila, estadoCuenta: { prestamos: [], abonos: [], movimientos: [] } });
        try {
            const empleado = Number(fila.idacceso || 0) > 0 ? String(fila.idacceso) : String(fila.usuario || "").trim();
            const respuesta = await GetRequest({
                url: apiPrestamosEstadoCuenta.replace("{usuarioempleado}", encodeURIComponent(empleado)),
                loader: "Consultando deducciones del empleado...",
            });
            if (respuesta.hasError) {
                handleOpenAlert("Error al consultar deducciones: " + respuesta.message, "error");
                return;
            }
            setDeduccionesDetalle({ fila, estadoCuenta: respuesta.result || { prestamos: [], abonos: [], movimientos: [] } });
        } catch (error) {
            handleOpenAlert("Error al consultar deducciones: " + error.message, "error");
        } finally {
            setLoadingDeducciones(false);
        }
    };

    const imprimirTicketNomina = async (fila) => {
        if (!fila) return;
        try {
            await enviarTicketNominaPrinterTools(fila);
            handleOpenAlert("Recibo enviado a la impresora", "success");
        } catch (error) {
            handleOpenAlert("Error al imprimir recibo: " + error.message, "error");
        }
    };

    const abrirImpresion = (titulo, contenido) => {
        const ventana = window.open("", "_blank", "width=860,height=600");
        if (!ventana) {
            handleOpenAlert("El navegador bloqueó la ventana de impresión", "warning");
            return;
        }
        ventana.document.write(`<!DOCTYPE html><html lang="es"><head><meta charset="utf-8"/><title>${titulo}</title>
        <style>
          * { font-family: Arial, Helvetica, sans-serif; }
          body { margin: 18px; color: #111; font-size: 12px; }
          h1, h2, h3, h4 { margin: 2px 0; }
          .center { text-align: center; }
          .right { text-align: right; }
          .bandera { border: 1px solid #333; padding: 10px; margin-bottom: 14px; page-break-inside: avoid; }
          table { width: 100%; border-collapse: collapse; margin-top: 8px; }
          th, td { border: 1px solid #333; padding: 4px 6px; font-size: 11px; }
          th { background: #eee; }
          .totales td { font-weight: 700; }
          .firma { width: 260px; border-top: 1px solid #333; margin-top: 34px; padding-top: 3px; text-align: center; }
        </style></head><body>${contenido}</body></html>`);
        ventana.document.close();
        ventana.focus();
        setTimeout(() => ventana.print(), 150);
    };

    const handleImprimirRecibos = async () => {
        if (!nominaRows || nominaRows.length === 0) {
            handleOpenAlert("Primero calcula la nómina", "warning");
            return;
        }
        try {
            for (const fila of nominaRows) {
                await enviarTicketNominaPrinterTools(fila);
            }
            handleOpenAlert(`${nominaRows.length} recibos enviados a impresión`, "success");
        } catch (error) {
            handleOpenAlert("Error al imprimir recibos: " + error.message, "error");
        }
    };

    const handleImprimirReporte = () => {
        if (!nominaRows || nominaRows.length === 0) {
            handleOpenAlert("Primero calcula la nómina", "warning");
            return;
        }
        const totales = nominaRows.reduce(
            (tot, fila) => ({
                nominabase: tot.nominabase + fila.nominabase,
                bono: tot.bono + fila.bono,
                comisiones: tot.comisiones + fila.comisiones,
                deducciones: tot.deducciones + (fila.deducciones || 0),
                total: tot.total + fila.total,
            }),
            { nominabase: 0, bono: 0, comisiones: 0, deducciones: 0, total: 0 }
        );

        const filasTabla = nominaRows
            .map(
                (fila, index) => `
                <tr>
                    <td>${index + 1}</td>
                    <td>${fila.usuario}</td>
                    <td>${fila.nombre}</td>
                    <td class="right">${fila.diasTrabajados}</td>
                    <td class="right">${fila.horasTotales.toFixed(2)}</td>
                    <td class="right">${formatCurrency(fila.nominabase)}</td>
                    <td class="right">${formatCurrency(fila.bono)}</td>
                    <td class="right">${formatCurrency(fila.comisiones)}</td>
                    <td class="right">${formatCurrency(fila.deducciones || 0)}</td>
                    <td class="right">${formatCurrency(fila.total)}</td>
                </tr>`
            )
            .join("");

        abrirImpresion("Reporte de Nóminas (Administración)", `
            <h3 class="center">REPORTE DE NOMINAS — ADMINISTRACION</h3>
            <p class="center">Periodo: ${periodoLabel()} — Generado: ${new Date().toLocaleString("es-MX")}</p>
            <table>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Clave Access</th>
                        <th>Empleado</th>
                        <th>Dias</th>
                        <th>Horas</th>
                        <th>Nomina</th>
                        <th>Bono</th>
                        <th>Comisiones</th>
                        <th>Deducciones</th>
                        <th>Total</th>
                    </tr>
                </thead>
                <tbody>${filasTabla}</tbody>
                <tfoot>
                    <tr class="totales">
                        <td colspan="3">TOTALES</td>
                        <td></td>
                        <td></td>
                        <td class="right">${formatCurrency(totales.nominabase)}</td>
                        <td class="right">${formatCurrency(totales.bono)}</td>
                        <td class="right">${formatCurrency(totales.comisiones)}</td>
                        <td class="right">${formatCurrency(totales.deducciones)}</td>
                        <td class="right">${formatCurrency(totales.total)}</td>
                    </tr>
                </tfoot>
            </table>
            <p class="center">Los recibos individuales se emiten con firma "Salvo buen cobro" como acuse de recibo de pago.</p>
        `);
    };

    const getBodyNominaPdf = ({ header = {}, filas = nominaRows || [], totalDias = null } = {}) => ({
        folio: header.folio ?? nominaInfo?.folio ?? 0,
        status: header.status ?? nominaInfo?.status ?? "",
        fechainicio: header.fechainicio || formatFechaConsulta(fechaInicio),
        fechafin: header.fechafin || formatFechaConsulta(fechaFin),
        usuario: usuarioData?.Usuario || "",
        salariodiario: 0,
        empleados: filas.map((fila) => ({
            idacceso: Number(fila.idacceso || 0),
            usuario: fila.usuario,
            nombre: fila.nombre,
            dias: fila.diasTrabajados ?? fila.dias ?? 0,
            horas: Number(Number(fila.horasTotales ?? fila.horas ?? 0).toFixed(2)),
            nominabase: Number(Number(fila.nominabase || 0).toFixed(2)),
            bono: Number(Number(fila.bono || 0).toFixed(2)),
            comisiones: Number(Number(fila.comisiones || 0).toFixed(2)),
            deducciones: Number(Number(fila.deducciones || 0).toFixed(2)),
            total: Number(Number(fila.total || 0).toFixed(2)),
            retardos: fila.retardos || 0,
        })),
    });

    const abrirReporteNominaPdf = async (body, loader = "Generando reporte PDF de nómina...") => {
        const abierto = await PostFileRequest({
            url: apiReporteNomina,
            body,
            openInNewTab: true,
            loader,
        });
        if (!abierto) {
            throw new Error("No se pudo generar el reporte en PDF");
        }
    };

    const handleImprimirNominaPdf = async () => {
        if (!nominaRows || nominaRows.length === 0) {
            handleOpenAlert("Primero calcula la nómina", "warning");
            return;
        }
        try {
            await abrirReporteNominaPdf(getBodyNominaPdf());
        } catch (error) {
            handleOpenAlert("Error al generar el PDF: " + error.message, "error");
        }
    };

    const reimprimirNominaHistorial = async (fila) => {
        const detalle = await consultarDetalle(fila);
        if (!detalle) return;
        const header = detalle.header || {};
        const filas = (detalle.detalle || []).map((empleado) => ({
            idacceso: Number(empleado.idacceso || empleado.id || 0),
            usuario: empleado.usuario || "",
            nombre: empleado.nombre || "",
            dias: empleado.dias || 0,
            horas: Number(empleado.horas || 0),
            nominabase: Number(empleado.nominabase || 0),
            bono: Number(empleado.bono || 0),
            comisiones: Number(empleado.comisiones || 0),
            total: Number(empleado.total || 0),
            retardos: empleado.retardos || 0,
        }));
        try {
            await abrirReporteNominaPdf(
                getBodyNominaPdf({
                    header: {
                        folio: fila.folio,
                        status: header.status || fila.status || "",
                        fechainicio: header.fechainicio || fila.fechainicio || "",
                        fechafin: header.fechafin || fila.fechafin || "",
                        salariodiario: header.salariodiario || salarioDiario || 0,
                    },
                    filas,
                }),
                "Reimprimiendo nómina..."
            );
        } catch (error) {
            handleOpenAlert("Error al reimprimir nómina: " + error.message, "error");
        }
    };

    const guardarSalarioEmpleado = async (fila, salario) => {
        const id = Number(fila.idacceso || 0);
        if (!id) {
            handleOpenAlert("No se pudo identificar al empleado para guardar su salario", "error");
            return false;
        }
        salariosPendientes.current += 1;
        try {
            const respuesta = await PostRequest({
                url: apiParametrosSave,
                body: { nombre: SALARIO_EMPLEADO_PREFIJO + id, valor: String(salario) },
                loader: "Guardando salario del empleado...",
            });
            if (respuesta.hasError || respuesta.result !== true) throw new Error(respuesta.message || "No se pudo guardar el salario");
            setUsuarios((prev) => (prev || []).map((u) => Number(u.id || u.idacceso) === id
                ? { ...u, salariodiario: salario } : u));
            setNominaRows((prev) => (prev || []).map((r) => {
                if (Number(r.idacceso) !== id) return r;
                const nominabase = (Number(r.horasTotales || 0) / horasJornada) * salario;
                return { ...r, salarioIndividual: true, salarioDiarioEmpleado: salario, nominabase,
                    total: Math.max(0, nominabase + (r.bono || 0) + (r.comisiones || 0) - (r.deducciones || 0)) };
            }));
            return true;
        } catch (error) {
            handleOpenAlert("No se pudo guardar el salario: " + error.message, "error");
            return false;
        } finally {
            salariosPendientes.current -= 1;
        }
    };

    const columnasEstaticas = [
        { title: "Clave Acceso", field: "usuario", width: 120 },
        { title: "Empleado", field: "nombre", width: 200 },
        { title: "Dias", field: "diasTrabajados", align: "center", width: 70 },
        { title: "Horas", field: "horasTotales", align: "center", width: 80 },
        { title: "Salario diario", field: "salarioDiarioEmpleado", align: "right", width: 120, sortable: false,
            render: (f) => <CeldaSalarioDiario fila={f} onGuardar={guardarSalarioEmpleado}
                deshabilitada={nominaInfo?.status === STATUS_TERMINADA} /> },
        { title: "Nomina", field: "nominabase", align: "right", render: (f) => formatCurrency(f.nominabase), width: 110 },
        { title: "Comisiones", field: "comisiones", align: "right", render: (f) => formatCurrency(f.comisiones), width: 110 },
        { title: "Total Nomina", field: "total", align: "right", render: (f) => formatCurrency(f.total), width: 120 },
    ];

    const columnasDeducciones = [
        { title: "Folio", field: "folioprestamo", align: "center", width: 70 },
        { title: "Fecha", field: "fechaprestamo", render: (f) => formatFechaHoraDisplay(f.fechaprestamo), width: 140 },
        { title: "Tipo", field: "tipoprestamo", width: 100 },
        { title: "Concepto", field: "concepto", width: 220 },
        { title: "Articulo", field: "clavearticulo", width: 110 },
        { title: "Med", field: "medida", align: "center", width: 70 },
        { title: "Monto", field: "montoprestamo", align: "right", render: (f) => formatCurrency(f.montoprestamo || 0), width: 100 },
        { title: "Abonado", field: "montoabonado", align: "right", render: (f) => formatCurrency(f.montoabonado || 0), width: 100 },
        { title: "Saldo", field: "saldopendiente", align: "right", render: (f) => formatCurrency(f.saldopendiente || 0), width: 100 },
    ];

    const accionesFila = (fila) => (
        <Stack direction="row" spacing={0.5}>
            <Button
                size="small"
                variant="outlined"
                startIcon={<TableViewIcon />}
                onClick={() => setChequeoFila(fila)}
            >
                Chequeos
            </Button>
            <Button
                size="small"
                variant="outlined"
                startIcon={<PaymentsIcon />}
                type="button"
                onClick={() => abrirDeduccionesEmpleado(fila)}
            >
                Deduc.
            </Button>
            <Button
                size="small"
                variant="outlined"
                startIcon={<VisibilityIcon />}
                type="button"
                onClick={() => abrirVistaPreviaTicketNomina(fila)}
            >
                Vista
            </Button>
            <Button
                size="small"
                variant="contained"
                startIcon={<PrintIcon />}
                type="button"
                onClick={() => imprimirTicketNomina(fila)}
            >
                Ticket
            </Button>
        </Stack>
    );

    const totalFila = (nominaRows || []).reduce((acc, fila) => acc + fila.total, 0);
    const prestamosActivosDeducciones = getPrestamosActivos(deduccionesDetalle?.estadoCuenta?.prestamos);
    const totalSaldoPrestamosActivos = prestamosActivosDeducciones.reduce(
        (total, prestamo) => total + Number(prestamo.saldopendiente || 0),
        0
    );

    const abrirPrestamoAutorizado = (_, usuarioValidado) => {
        setUsuarioAutorizaPrestamo(usuarioValidado || "");
        setOpenPrestamo(true);
    };

    const refrescarPrestamosNomina = async () => {
        setOpenPrestamo(false);
        await handleCalcular();
    };

    return (
        <SizeSoftXLayoutForm
            titlePage="Administración de Nóminas"
            buttonActions={
                <MnuNominasActions
                    onSalir={handleClickRegresarMainDashboard}
                    onCalcular={handleCalcular}
                    onNueva={handleNueva}
                    onGuardar={nominaRows?.length ? handleGuardar : null}
                    onAplicar={nominaRows?.length ? handleAplicar : null}
                    onBuscarNominas={abrirHistorial}
                    onImprimirRecibos={nominaRows?.length ? handleImprimirRecibos : null}
                    onImprimirResumen={nominaRows?.length ? handleImprimirNominaPdf : null}
                    onPrestamos={() => setOpenLoginPrestamo(true)}
                    onEliminar={handleEliminarNomina}
                    eliminarDisabled={!nominaEliminable || !nominaInfo?.id}
                    cargando={loading}
                />
            }
        >
            <Box sx={{ p: 2 }}>
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                        <Typography variant="h6">
                            Periodo de nómina
                        </Typography>
                        <TextField autoComplete="off"
                            label="Folio Nómina:"
                            size="small"
                            value={nominaInfo?.folio || 0}
                            sx={{ minWidth: 120 }}
                            disabled
                        />
                        <SemaforoStatus status={nominaInfo?.status || ""} />
                    </Stack>
                    <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="flex-end">
                        <Box>
                            <DatePicker
                                selected={fechaInicio}
                                onChange={(date) => setFechaInicio(normalizeStartDate(date))}
                                dateFormat="dd/MM/yyyy"
                                customInput={<CustomDateInput label="Fecha inicio" />}
                            />
                        </Box>
                        <Box>
                            <DatePicker
                                selected={fechaFin}
                                onChange={(date) => setFechaFin(normalizeEndDate(date))}
                                dateFormat="dd/MM/yyyy"
                                customInput={<CustomDateInput label="Fecha fin" />}
                            />
                        </Box>
                        <Box>
                            <TextField autoComplete="off"
                                label="Salario diario general ($)"
                                size="small"
                                type="text"
                                inputMode="numeric"
                                enterKeyHint="enter"
                                value={salarioDiario}
                                onChange={handleChangeSalarioDiario}
                                sx={{ minWidth: 200 }}
                            />
                        </Box>
                        <Box>
                            <TextField autoComplete="off"
                                label="Horas por jornada"
                                size="small"
                                type="text"
                                inputMode="numeric"
                                enterKeyHint="enter"
                                value={horasJornada}
                                onChange={handleChangeHorasJornada}
                                sx={{ minWidth: 180 }}
                            />
                        </Box>
                        <Box sx={{ mt: 1 }}>
                            <ConceptosEmpleadoEditor
                                esquema={esquemaBonos}
                                onCambiarEsquema={handleCambiarEsquema}
                            />
                        </Box>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                        El salario general se usa cuando el empleado no tiene uno individual. La jornada es de ${horasJornada} horas.
                        La entrada es a las 09:15 a més tardar. Los bonos y deducciones se capturan directamente en la tabla de resultados.
                    </Typography>
                </Paper>

                {nominaRows !== null && (
                    <Paper sx={{ p: 2 }}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="h6">Resultado del periodo</Typography>
                            <Chip label={periodoLabel()} variant="outlined" size="small" />
                            <Chip label={`Empleados: ${nominaRows.length}`} size="small" />
                            <Chip label={`Total nómina: ${formatCurrency(totalFila)}`} color="primary" size="small" />
                        </Stack>
                        <SizeSoftXTablaNominasEditable
                            columns={columnasEstaticas}
                            esquema={esquemaBonos}
                            data={nominaRows}
                            actions={accionesFila}
                            onEditarConcepto={handleEditarConceptoEmpleado}
                            disablePagination
                            searchPlaceholder="Buscar empleado o clave..."
                        />
                    </Paper>
                )}
            </Box>

            <FrmLoginValidate
                openModal={openLoginPrestamo}
                tiposUsuario={["SA", "ADMINISTRADOR", "SUPERVISOR"]}
                handleComandoEjecucion={abrirPrestamoAutorizado}
                hancleCloseModalValidator={() => setOpenLoginPrestamo(false)}
            />
            <ModalPrestamoEmpleado
                open={openPrestamo}
                onClose={() => setOpenPrestamo(false)}
                onSaved={refrescarPrestamosNomina}
                usuarioAutoriza={usuarioAutorizaPrestamo}
            />

            <ModalDetalleChequeosNominas
                open={Boolean(chequeoFila)}
                onClose={() => setChequeoFila(null)}
                fila={chequeoFila}
                onImprimirTicket={imprimirTicketNomina}
                onVistaPreviaTicket={abrirVistaPreviaTicketNomina}
            />

            <ModalHistorialNominas
                open={historialAbierto}
                onClose={() => setHistorialAbierto(false)}
                rows={historialRows}
                cargando={cargandoHistorial}
                onEditar={editarDesdeHistorial}
                onVerDetalle={verDetalle}
                onImprimir={reimprimirNominaHistorial}
            />

            <DetalleNominaModal
                open={Boolean(detalleModal)}
                onClose={() => setDetalleModal(null)}
                detalle={detalleModal}
            />

            <SizeSoftXModal
                open={Boolean(deduccionesDetalle)}
                onClose={() => setDeduccionesDetalle(null)}
                title="Detalle de deducciones"
                maxWidth="lg"
                success="Imprimir ticket"
                onSuccess={imprimirTicketDeducciones}
                disableSuccess={loadingDeducciones}
                cancel="Cerrar"
            >
                <Stack spacing={2}>
                    <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {deduccionesDetalle?.fila?.nombre || ""}
                        </Typography>
                        <Chip label={`Periodo: ${periodoLabel()}`} size="small" />
                        <Chip
                            label={`Deducciones: ${formatCurrency(deduccionesDetalle?.fila?.deducciones || 0)}`}
                            color="primary"
                            size="small"
                        />
                        <Chip
                            label={`Saldo préstamos: ${formatCurrency(totalSaldoPrestamosActivos)}`}
                            color={totalSaldoPrestamosActivos > 0 ? "warning" : "default"}
                            size="small"
                        />
                    </Stack>

                    <SizeSoftXTablaNominas
                        id="folioprestamo"
                        columns={columnasDeducciones}
                        data={prestamosActivosDeducciones}
                        disablePagination
                        searchPlaceholder="Buscar préstamo..."
                        emptyMessage={loadingDeducciones ? "Consultando préstamos..." : "No hay préstamos activos para deducir"}
                    />
                </Stack>
            </SizeSoftXModal>
            <SizeSoftXConfirmDialog
                open={confirmEliminar}
                title="ELIMINAR NÓMINA"
                message={`¿Deseas eliminar la nómina actual (folio ${nominaInfo?.folio || 0})? Esta acción no se puede deshacer.`}
                handleConfirmDialog={handleConfirmEliminar}
                colorTitle="#D32F2F"
            />
        </SizeSoftXLayoutForm>
    );
}
